import React, { useCallback, useEffect, useState } from "react";
import _ from "lodash";
import ActivityOverviewHeader from "../ActivityOverviewHeader/ActivityOverviewHeader";
import ApiClient from "../../../services/ApiClient";
import { ACTIVITY_OVERDUE } from "../../../services/Activities/ActivityStatus";
import { buildApiFilters, buildSearchParams } from "../../../services/Activities/activityFunctions";
import useActivitiesFetching from "../../../hooks/useActivitiesFetching";
import PageContentContainer from "../../../components/Container/PageContentContainer";
import OverviewActivityEntry from "./OverviewActivityEntry";
import { Box, Divider } from "@mui/material";
import activityOverviewStyle from "./activityOverviewStyle";
import { deadlineActivityTypes } from "../../../services/Activities/activityTypes";
import { useBackofficeUser } from "../../../provider/BackofficeUserProvider";
import { Activity } from "../../../types/Activity";
import { BackofficeUser } from "../../../types/BackofficeUser";
import dayjs from "dayjs";

const DeadlineOverview = () => {
  const { lawyers, backofficeUsers } = useBackofficeUser();

  const [productFilter, setProductFilter] = useState("all");
  const [activityTypeFilter, setActivityTypeFilter] = useState("allDeadlines");
  const [userFilter, setUserFilter] = useState("all");
  const [timespanFilter, setTimespanFilter] = useState("today");
  const [userActivitiesToday, setUserActivitiesToday] = useState<Activity[]>([]);
  const [refreshCounter, setRefreshCounter] = useState(0);

  const dataSource = buildSearchParams(
    _.merge(
      buildApiFilters({
        productFilter: productFilter,
        activityTypeFilter: activityTypeFilter,
        userFilter: userFilter,
        timespanFilter: timespanFilter,
      }),
      {
        refreshCounter: refreshCounter,
      }
    )
  );

  let dataSourceString = dataSource.toString();

  if (userFilter === "all") {
    _.forEach(lawyers, (lawyer) => {
      dataSourceString += "&assignedUser[]=" + lawyer["@id"];
    });
  }

  const { activities, isLoading } = useActivitiesFetching(dataSourceString, backofficeUsers);

  const postProcessFilterActivities = useCallback(
    (activities: Activity<BackofficeUser>[]) => {
      if (timespanFilter === "overdue") {
        activities = _.filter(activities, ["activityStatus", ACTIVITY_OVERDUE]);
      }
      return activities;
    },
    [timespanFilter]
  );

  const updateUserActivitiesToday = useCallback(async () => {
    if (userFilter === "all") {
      setUserActivitiesToday([]);
      return;
    }
    const filters: Record<string, any> = {
      assignedUser: userFilter,
      "dueDate[before]": dayjs().format("YYYY-MM-DD"),
      type: deadlineActivityTypes,
      done: false,
      deleted: false,
      pagination: false,
    };

    let searchParams = new URLSearchParams();
    _.map(filters, (value, key) => {
      searchParams.append(key, value);
    });

    const userActivitiesResult = await ApiClient.get("activities?" + searchParams.toString());
    setUserActivitiesToday(userActivitiesResult["hydra:member"]);
  }, [userFilter]);

  const updateActivities = () => {
    setRefreshCounter(refreshCounter + 1);
  };

  useEffect(() => {
    updateUserActivitiesToday();
  }, [userFilter, updateUserActivitiesToday]);

  const filteredActivities = isLoading || !activities ? [] : postProcessFilterActivities(activities);

  if (lawyers.length < 1) {
    return null;
  }

  const overdueTotal = userActivitiesToday.reduce((total, activity) => {
    return activity.activityStatus === ACTIVITY_OVERDUE ? total + 1 : total;
  }, 0);

  return (
    <PageContentContainer>
      <ActivityOverviewHeader
        todayTotal={userActivitiesToday.length}
        overdueTotal={overdueTotal}
        setProductFilter={(data) => {
          updateActivities();
          setProductFilter(data);
        }}
        productFilter={productFilter}
        setActivityTypeFilter={(data: string) => {
          updateActivities();
          setActivityTypeFilter(data);
        }}
        activityTypeFilter={activityTypeFilter}
        userFilter={userFilter}
        setUserFilter={(data: string) => {
          updateActivities();
          setUserFilter(data);
        }}
        timespanFilter={timespanFilter}
        setTimespanFilter={(data: string) => {
          updateActivities();
          setTimespanFilter(data);
        }}
        isLoading={isLoading}
        agents={lawyers}
        isDeadlineView
      />
      <Box sx={activityOverviewStyle.headline}>Fristenkalender</Box>
      {_.map(filteredActivities, (activity, index) => (
        <React.Fragment key={activity.id}>
          <OverviewActivityEntry activity={activity} update={updateActivities} />
          {index !== filteredActivities.length - 1 && (
            <Box sx={activityOverviewStyle.divider}>
              <Divider />
            </Box>
          )}
        </React.Fragment>
      ))}
    </PageContentContainer>
  );
};

export default DeadlineOverview;
